import { cva } from 'class-variance-authority'
import { Pmp_Configs } from '@/gql/generated/graphql'
import { BodyText } from 'design-configuration/components/responsive-text'
import { cn } from '@/lib/utils'

const formatPMPTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return 'Always available'

  const date = new Date(timestamp)
  const now = new Date()

  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear()
  const time = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  })

  if (date < now) {
    return `Customization expired on ${day} ${month} ${year}, ${time}`
  }

  return `${day} ${month} ${year}, ${time}`
}

const tooltipContentVariants = cva('', {
  variants: {
    colorTheme: {
      primary: 'bg-surface',
      'primary-inverted': 'bg-surface-container-inverted'
    }
  },
  defaultVariants: {
    colorTheme: 'primary'
  }
})

const tooltipLabelVariants = cva('', {
  variants: {
    colorTheme: {
      primary: 'text-on-surface',
      'primary-inverted': 'text-on-surface-inverted'
    }
  },
  defaultVariants: {
    colorTheme: 'primary'
  }
})

const tooltipTagVariants = cva('', {
  variants: {
    colorTheme: {
      primary: 'border-outline-variant',
      'primary-inverted': 'border-outline-inverted'
    }
  },
  defaultVariants: {
    colorTheme: 'primary'
  }
})

export const PmpCustomizableDetails = ({
  pmpDetails,
  colorTheme = 'primary'
}: {
  pmpDetails: Pmp_Configs
  colorTheme?: 'primary' | 'primary-inverted'
}): React.ReactElement => {
  const { auth_option, pmp_locked_after_timestamp } = pmpDetails
  const authLabels = []
  if (auth_option.includes('Owner')) {
    authLabels.push('Artwork owner')
  }
  if (auth_option.includes('Artist')) {
    authLabels.push('Artist')
  }
  if (auth_option.includes('Address')) {
    authLabels.push('Contract address')
  }

  const formattedTimestamp = formatPMPTimestamp(pmp_locked_after_timestamp)

  return (
    <div className={tooltipContentVariants({ colorTheme })}>
      <div>
        <BodyText className={cn('text-sm font-medium', tooltipLabelVariants({ colorTheme }))}>
          Customizable by:
        </BodyText>
        <div className="gap-x-xs mt-2xs flex">
          {authLabels.map((label) => (
            <div
              key={label}
              className={cn('px-xs cursor-pointer rounded-full border py-[2px]', tooltipTagVariants({ colorTheme }))}
            >
              <BodyText className={cn('text-sm', tooltipLabelVariants({ colorTheme }))}>{label}</BodyText>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-xs">
        <BodyText className={cn('text-sm font-medium', tooltipLabelVariants({ colorTheme }))}>
          Customizable until:
        </BodyText>
        <BodyText className={cn('text-sm', tooltipLabelVariants({ colorTheme }))}>{formattedTimestamp}</BodyText>
      </div>
    </div>
  )
}
